import {
  DXF_BASEMAP_CONTEXT_ASSET_FORMAT,
  DXF_LEGACY_RAW_ASSET_FORMAT,
  FLOOR_PLAN_CONFIG_VERSION,
  type IDxfFloorPlanConfig,
  type IFloorPlanConfig,
  type IPdfFloorPlanConfig,
  type TDxfFloorPlanAssetFormat,
  type TFloorPlanType
} from '@/editor/types';
import { DXF_BASEMAP_CONTEXT_VERSION } from '@/editor/floor-plan/dxf/parser-next/types';
import { isObject } from '@/utils/types/assert';

type RawFloorPlanConfig = Record<string, unknown>;

interface NormalizeFloorPlanConfigOptions {
  id?: string;
  type?: string;
  name?: string;
  url?: string;
  scale?: number;
}

function getString(value: unknown) {
  return typeof value === 'string' ? value : undefined;
}

function getNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function getBoolean(value: unknown) {
  return typeof value === 'boolean' ? value : undefined;
}

function getStringArray(value: unknown) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : undefined;
}

function getConfig(raw: RawFloorPlanConfig | null | undefined) {
  return raw && isObject(raw.config) ? raw.config : {};
}

function getFloorPlanType(type: string | undefined): TFloorPlanType | null {
  if (type === 'dxf' || type === 'pdf') return type;
  return null;
}

function getAssetFormat(value: unknown): TDxfFloorPlanAssetFormat | undefined {
  if (value === DXF_LEGACY_RAW_ASSET_FORMAT || value === DXF_BASEMAP_CONTEXT_ASSET_FORMAT) return value;
  return undefined;
}

export function getPdfFloorPlanScale(floorPlanConfig: IFloorPlanConfig | null | undefined) {
  if (!floorPlanConfig || floorPlanConfig.type !== 'pdf') return 1;
  return floorPlanConfig.config.scale;
}

export function getDxfFloorPlanHiddenLayers(floorPlanConfig: IFloorPlanConfig | null | undefined) {
  if (!floorPlanConfig || floorPlanConfig.type !== 'dxf') return [];
  return floorPlanConfig.config.dxfHiddenLayers ?? [];
}

export function getDxfFloorPlanRenderSolid(floorPlanConfig: IFloorPlanConfig | null | undefined) {
  if (!floorPlanConfig || floorPlanConfig.type !== 'dxf') return false;
  return floorPlanConfig.config.renderSolid ?? false;
}

export function isLegacyRawDxfFloorPlan(floorPlanConfig: IFloorPlanConfig | null | undefined) {
  return floorPlanConfig?.type === 'dxf' && floorPlanConfig.config.assetFormat === DXF_LEGACY_RAW_ASSET_FORMAT;
}

export function isCompressedDxfFloorPlan(floorPlanConfig: IFloorPlanConfig | null | undefined) {
  return floorPlanConfig?.type === 'dxf' && floorPlanConfig.config.assetFormat === DXF_BASEMAP_CONTEXT_ASSET_FORMAT;
}

export function normalizeFloorPlanConfig(
  rawFloorPlanConfig: unknown,
  options: NormalizeFloorPlanConfigOptions = {}
): IFloorPlanConfig | null {
  const raw = isObject(rawFloorPlanConfig) ? rawFloorPlanConfig : null;
  const config = getConfig(raw);
  const type = getFloorPlanType(options.type ?? getString(raw?.type));

  if (!raw || !type) return null;

  const id = options.id ?? getString(raw.id);
  if (!id) return null;

  const name = options.name ?? getString(raw.name) ?? '';
  const url = options.url ?? getString(raw.url);

  if (type === 'pdf') {
    const scale = getNumber(config.scale) ?? getNumber(raw.scale) ?? options.scale ?? 1;
    return {
      id,
      type,
      name,
      ...(url ? { url } : {}),
      version: FLOOR_PLAN_CONFIG_VERSION,
      config: {
        scale
      }
    } satisfies IPdfFloorPlanConfig;
  }

  const assetFormat = getAssetFormat(config.assetFormat) ?? DXF_LEGACY_RAW_ASSET_FORMAT;
  const isCompressed = assetFormat === DXF_BASEMAP_CONTEXT_ASSET_FORMAT;
  const hiddenLayers =
    getStringArray(config.dxfHiddenLayers) ??
    getStringArray(raw.dxfHiddenLayers) ??
    getStringArray(raw.hiddenDxfLayers) ??
    [];

  return {
    id,
    type,
    name,
    ...(url ? { url } : {}),
    version: FLOOR_PLAN_CONFIG_VERSION,
    config: {
      assetFormat,
      ...(isCompressed
        ? {
            contextVersion: getNumber(config.contextVersion) ?? DXF_BASEMAP_CONTEXT_VERSION,
            compression: 'gzip' as const
          }
        : {}),
      transformOcs: getBoolean(config.transformOcs) ?? (isCompressed ? true : false),
      renderSolid: getBoolean(config.renderSolid) ?? false,
      dxfHiddenLayers: hiddenLayers
    }
  } satisfies IDxfFloorPlanConfig;
}

export function toPersistedFloorPlanConfig(
  floorPlanConfig: IFloorPlanConfig | null | undefined,
  options: { dxfHiddenLayers?: string[] } = {}
): IFloorPlanConfig | null {
  if (!floorPlanConfig) return null;

  if (floorPlanConfig.type === 'pdf') {
    return {
      id: floorPlanConfig.id,
      type: floorPlanConfig.type,
      name: floorPlanConfig.name,
      version: FLOOR_PLAN_CONFIG_VERSION,
      config: {
        scale: floorPlanConfig.config.scale
      }
    };
  }

  return {
    id: floorPlanConfig.id,
    type: floorPlanConfig.type,
    name: floorPlanConfig.name,
    version: FLOOR_PLAN_CONFIG_VERSION,
    config: {
      ...floorPlanConfig.config,
      dxfHiddenLayers: options.dxfHiddenLayers ?? floorPlanConfig.config.dxfHiddenLayers ?? []
    }
  };
}
